import {
  EditorElement,
  EditorReactComponent,
  ElementItem,
  Style,
} from '@wix/component-protocol/schema';
import { StylesDerivedNativeActionNames } from '@wix/editor-platform-host-integration-apis';
import { objectFilter, objectMap } from '../../../../utils';
import { ALLOWED_CSS_PROPERTY_TYPES_BY_ACTION_NAME } from '../../constants';
import { ManifestProcessor } from '../../types';
import { ManifestRuntimeContextBuilder } from '@wix/editor-platform-manifest-services';
import { EditorPointer } from '@wix/editorx-core-api';
import { CompRef } from '@wix/document-services-types';
import { CssPropertyType } from '@wix/ambassador-devcenter-v1-component-type-data/types';

const filterStylesByActionName = (
  styleProperties: Style,
  actionName: StylesDerivedNativeActionNames,
): Style => {
  const allowedStyles: readonly CssPropertyType[] =
    ALLOWED_CSS_PROPERTY_TYPES_BY_ACTION_NAME[actionName];

  if (!allowedStyles) {
    return {};
  }

  return objectFilter(styleProperties, (value) => {
    const cssPropertyType = value?.cssPropertyType;
    return !!cssPropertyType && allowedStyles.includes(cssPropertyType);
  });
};

const processElements = (
  elements: Record<string, ElementItem>,
  actionName: StylesDerivedNativeActionNames,
  builder: ManifestRuntimeContextBuilder,
  editorPointer: EditorPointer<CompRef>,
) => {
  objectMap(elements, (element, elementId) => {
    if (element?.elementType === 'inlineElement' && element.inlineElement) {
      // TODO: use editorPointerAPI to build editor pointer
      const elementEditorPointer: EditorPointer<CompRef> = {
        ...editorPointer,
        innerElementPath: editorPointer.innerElementPath
          ? [...editorPointer.innerElementPath, elementId]
          : [elementId],
      };
      processElement(
        element.inlineElement,
        actionName,
        builder,
        elementEditorPointer,
      );
    }
  });
};

const processElement = (
  elementManifestStructure: EditorElement,
  actionName: StylesDerivedNativeActionNames,
  builder: ManifestRuntimeContextBuilder,
  editorPointer: EditorPointer<CompRef>,
) => {
  // ignore everything that is not style or elements
  const relevantStructure = {
    style: elementManifestStructure.style,
    elements: elementManifestStructure.elements,
  };

  let filteredStyles = {};
  if (relevantStructure.style) {
    filteredStyles = {
      style: filterStylesByActionName(relevantStructure.style, actionName),
    };
  }

  builder.withManifest(editorPointer, {
    editorElement: {
      ...relevantStructure,
      ...filteredStyles,
    },
  });

  if (relevantStructure?.elements) {
    processElements(
      relevantStructure.elements,
      actionName,
      builder,
      editorPointer,
    );
  }
};

const defaultStrategy: ManifestProcessor = (
  manifest: EditorReactComponent,
  actionName: StylesDerivedNativeActionNames,
  builder: ManifestRuntimeContextBuilder,
  editorPointer: EditorPointer<CompRef>,
) => {
  if (manifest?.editorElement) {
    processElement(manifest?.editorElement, actionName, builder, editorPointer);
  }
};

export default defaultStrategy;
